<template>
  <span>
    <!-- Botón disparador (opcional, para reutilizar el modal desde otras vistas) -->
    <button
      v-if="show_button"
      type="button"
      :class="`btn btn-sm btn-ai-quick ${button_class}`"
      @click="open"
    >
      <img src="@/assets/icon-ai.svg" alt="" class="icon-btn-ai mr-1" />
      {{ $t("reusable.aiMatterBuilderModal.openButton") }}
    </button>

    <!-- Modal (BS4 manual, sin Bootstrap-Vue) -->
    <div v-if="visible" class="ai-builder-overlay">
      <div class="ai-builder-dialog" role="dialog" aria-modal="true">
        <div class="modal-content ai-builder-content">
          <div class="modal-header py-2">
            <h5 class="modal-title">
              <img src="@/assets/icon-ai.svg" alt="" class="icon-btn-ai mr-1" />
              {{
                $t("reusable.aiMatterBuilderModal.title", {
                  entity: $getVisibleNames(
                    "manual.matter_program",
                    false,
                    $t("reusable.aiMatterBuilderModal.matterProgramLabel")
                  ),
                })
              }}
            </h5>
            <button
              type="button"
              class="close"
              :aria-label="$t('reusable.aiMatterBuilderModal.close')"
              @click="close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body ai-builder-body">
            <!-- Error -->
            <div v-if="error" class="alert alert-danger py-2">
              {{ error }}
            </div>

            <!-- Cargando contexto / generación inicial -->
            <div v-if="loading" class="text-center my-5">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 text-muted">
                {{ $t("reusable.aiMatterBuilderModal.generating") }}
              </p>
            </div>

            <template v-else>
              <!-- Indicador de generación por streaming -->
              <div
                v-if="generating"
                class="ai-builder-generating d-flex align-items-center mb-3"
              >
                <div
                  class="spinner-border spinner-border-sm mr-2"
                  role="status"
                ></div>
                <span>{{
                  $t("reusable.aiMatterBuilderModal.generating")
                }}</span>
              </div>

              <!-- Descriptores de la asignatura -->
              <h6 class="ai-builder-section-title">
                {{
                  $t("reusable.aiMatterBuilderModal.descriptors", {
                    entity: $getVisibleNames(
                      "mesh.egressprofilematter",
                      false,
                      $t("reusable.aiMatterBuilderModal.matterLabel"),
                      true
                    ),
                  })
                }}
              </h6>
              <p v-if="descriptors.length == 0" class="text-muted small">
                {{
                  $t("reusable.aiMatterBuilderModal.noDescriptors", {
                    entity: $getVisibleNames(
                      "mesh.egressprofilematter",
                      false,
                      $t("reusable.aiMatterBuilderModal.matterLabel"),
                      true
                    ),
                  })
                }}
              </p>
              <AiBuilderItemCard
                v-for="item in descriptors"
                :key="`descriptor-${item.descriptor}`"
                :item="item"
                :busy="busy"
                @remove="item.deleted = true"
                @restore="restoreDescriptor(item)"
                @regenerate="regenerateDescriptor(item)"
              >
                <template #header>
                  <strong>{{ item.title }}</strong>
                </template>
                <template #body>
                  <p class="mb-1 mt-1 ai-builder-description">
                    {{ item.description }}
                  </p>
                </template>
              </AiBuilderItemCard>
            </template>
          </div>

          <div class="modal-footer py-2">
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              :disabled="saving"
              @click="close"
            >
              {{ $t("reusable.aiMatterBuilderModal.cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              :disabled="busy || loading || !hasPendingItems"
              @click="save"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm mr-1"
                role="status"
              ></span>
              {{ $t("reusable.aiMatterBuilderModal.confirmSave") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </span>
</template>

<script>
import { ref, computed, getCurrentInstance } from "vue";
import * as names from "@/store/names";
import { toast } from "@/utils/utils";
import useAiBuilderStream from "./ai-builder/useAiBuilderStream";
import AiBuilderItemCard from "./ai-builder/AiBuilderItemCard";

export default {
  name: "AiMatterBuilderModal",
  components: { AiBuilderItemCard },
  props: {
    egress_profile_matter_id: {
      type: Number,
      required: true,
    },
    show_button: {
      type: Boolean,
      default: false,
    },
    button_class: {
      type: String,
      default: "",
    },
  },
  setup(props, { emit }) {
    const proxy = getCurrentInstance().proxy;
    const store = proxy.$store;

    const visible = ref(false);
    const loading = ref(false);
    const generating = ref(false);
    const saving = ref(false);
    const error = ref(null);
    const descriptors = ref([]);
    // Datos del EgressProfileMatter, cargados al abrir: definen dónde vive
    // el contenido (mismo criterio que MatterProgram.vue).
    const matter_id = ref(null);
    const use_matter_values = ref(false);

    const school_descriptors = computed(
      () => store.getters[names.EGRESS_PROFILE_MATTER_DESCRIPTORS]
    );
    // Contenido existente según la fuente que corresponda:
    // use_matter_values=false → EPMMiddleDescriptor (por plan);
    // use_matter_values=true  → MMiddleDescriptor (Asignatura Base).
    const middle_descriptors = computed(() => {
      if (use_matter_values.value) {
        return store.getters[names.M_MIDDLE_DESCRIPTORS].filter(
          (x) => x.matter == matter_id.value
        );
      }
      return store.getters[names.EPM_MIDDLE_DESCRIPTORS].filter(
        (x) => x.egress_profile_matter == props.egress_profile_matter_id
      );
    });

    const busy = computed(
      () =>
        saving.value ||
        generating.value ||
        descriptors.value.some((x) => x.regenerating)
    );
    function isDescriptorChanged(item) {
      return item.existing && item.description !== item.original_description;
    }
    const hasPendingItems = computed(() =>
      descriptors.value.some((x) =>
        x.existing
          ? x.deleted || isDescriptorChanged(x)
          : !x.deleted && x.description
      )
    );

    // Conversación con el endpoint builder (SSE + manejo de 403):
    // lógica compartida con los demás builders de dominio.
    const { callBuilder } = useAiBuilderStream({
      url: `/common/matter-builder-ai/`,
      generate_error_key: "reusable.aiMatterBuilderModal.generateError",
    });

    function pushGeneratedDescriptor(item) {
      const school_descriptor = school_descriptors.value.find(
        (x) => x.id == item.descriptor
      );
      if (school_descriptor == null) return;
      const current = descriptors.value.find(
        (x) => x.descriptor == item.descriptor
      );
      if (current != null) {
        if (!current.existing) current.description = item.description;
        return;
      }
      descriptors.value.push({
        descriptor: school_descriptor.id,
        title: school_descriptor.title,
        description: item.description,
        existing: false,
        deleted: false,
        theme: "",
        regenerating: false,
      });
    }

    function buildExistingDescriptors() {
      return school_descriptors.value.reduce((list, descriptor) => {
        const middle = middle_descriptors.value.find(
          (x) =>
            x.descriptor == descriptor.id &&
            x.description != null &&
            x.description.trim() != ""
        );
        if (middle == null) return list;
        // Los existentes pueden venir con HTML del editor de texto: se
        // muestran como texto plano.
        const description = proxy.$htmlToPlainText(middle.description);
        list.push({
          descriptor: descriptor.id,
          title: descriptor.title,
          description,
          existing: true,
          middle_id: middle.id,
          original_description: description,
          deleted: false,
          theme: "",
          regenerating: false,
        });
        return list;
      }, []);
    }

    function reconcile(data) {
      // El resultado final validado del backend es autoritativo; se
      // preservan las marcas del usuario (deleted/theme) hechas durante
      // el streaming.
      const generated = data.descriptors || [];
      const prev_descriptors = descriptors.value;
      descriptors.value = school_descriptors.value.reduce(
        (list, descriptor) => {
          const middle = middle_descriptors.value.find(
            (x) =>
              x.descriptor == descriptor.id &&
              x.description != null &&
              x.description.trim() != ""
          );
          const ai_descriptor = generated.find(
            (x) => x.descriptor == descriptor.id
          );
          if (middle == null && ai_descriptor == null) return list;
          const prev = prev_descriptors.find(
            (x) => x.descriptor == descriptor.id
          );
          const description = middle
            ? proxy.$htmlToPlainText(middle.description)
            : ai_descriptor.description;
          list.push({
            descriptor: descriptor.id,
            title: descriptor.title,
            description,
            existing: middle != null,
            middle_id: middle ? middle.id : null,
            original_description: middle ? description : null,
            deleted: prev ? prev.deleted : false,
            theme: prev ? prev.theme : "",
            regenerating: false,
          });
          return list;
        },
        []
      );
    }

    async function generateAll() {
      // Los descriptores con contenido ya existente se muestran de
      // inmediato, tal cual; el resto llega por streaming.
      descriptors.value = buildExistingDescriptors();
      const data = await callBuilder(
        {
          mode: "all",
          egress_profile_matter: props.egress_profile_matter_id,
        },
        (event) => {
          if (event.type === "descriptor") pushGeneratedDescriptor(event.item);
        }
      );
      reconcile(data);
    }

    async function open() {
      visible.value = true;
      error.value = null;
      loading.value = true;
      descriptors.value = [];
      try {
        const ep_matter = await store.dispatch(
          names.FETCH_EGRESS_PROFILE_MATTER,
          props.egress_profile_matter_id
        );
        matter_id.value = ep_matter.matter;
        use_matter_values.value = ep_matter.use_matter_values;
        const fetches = [
          use_matter_values.value
            ? store.dispatch(names.FETCH_M_MIDDLE_DESCRIPTORS, {
                matter_id: matter_id.value,
              })
            : store.dispatch(names.FETCH_EPM_MIDDLE_DESCRIPTORS, {
                egress_profile_matter_id: props.egress_profile_matter_id,
              }),
        ];
        // El catálogo de descriptores es institucional (no depende de la
        // asignatura): si ya está en el store, no se vuelve a pedir.
        if (school_descriptors.value.length === 0) {
          fetches.push(
            store.dispatch(names.FETCH_EGRESS_PROFILE_MATTER_DESCRIPTORS, {})
          );
        }
        await Promise.all(fetches);
        // El contenido llega por streaming: se muestra a medida que se
        // genera, con un indicador liviano en vez del spinner de pantalla.
        loading.value = false;
        generating.value = true;
        await generateAll();
      } catch (e) {
        error.value =
          e.message ||
          proxy.$t("reusable.aiMatterBuilderModal.generateError");
      } finally {
        loading.value = false;
        generating.value = false;
      }
    }

    function close() {
      if (saving.value) return;
      visible.value = false;
      emit("closed");
    }

    function restoreDescriptor(item) {
      item.deleted = false;
      // Al volver a añadir un descriptor sin contenido, se regenera.
      if (!item.description) regenerateDescriptor(item);
    }

    async function regenerateDescriptor(item) {
      item.regenerating = true;
      error.value = null;
      try {
        const data = await callBuilder({
          mode: "descriptor",
          egress_profile_matter: props.egress_profile_matter_id,
          descriptor: item.descriptor,
          theme: item.theme,
        });
        const generated = (data.descriptors || []).find(
          (x) => x.descriptor == item.descriptor
        );
        if (generated) {
          item.description = generated.description;
          item.deleted = false;
        }
      } catch (e) {
        error.value = e.message;
      } finally {
        item.regenerating = false;
      }
    }

    async function save() {
      saving.value = true;
      error.value = null;
      try {
        let order = middle_descriptors.value.length;
        for (const item of descriptors.value) {
          if (item.existing) {
            if (item.deleted) {
              await store.dispatch(
                use_matter_values.value
                  ? names.DELETE_M_MIDDLE_DESCRIPTORS
                  : names.DELETE_EPM_MIDDLE_DESCRIPTORS,
                item.middle_id
              );
            } else if (isDescriptorChanged(item)) {
              await store.dispatch(
                use_matter_values.value
                  ? names.PATCH_M_MIDDLE_DESCRIPTORS
                  : names.PATCH_EPM_MIDDLE_DESCRIPTORS,
                {
                  id: item.middle_id,
                  item: { description: item.description },
                }
              );
            }
            continue;
          }
          if (item.deleted || !item.description) continue;
          order += 1;
          if (use_matter_values.value) {
            await store.dispatch(names.POST_M_MIDDLE_DESCRIPTORS, {
              matter: matter_id.value,
              descriptor: item.descriptor,
              description: item.description,
              order,
            });
          } else {
            await store.dispatch(names.POST_EPM_MIDDLE_DESCRIPTORS, {
              egress_profile_matter: props.egress_profile_matter_id,
              descriptor: item.descriptor,
              description: item.description,
              order,
            });
          }
        }
        toast(proxy.$t("reusable.aiMatterBuilderModal.savedToast"));
        emit("saved");
        visible.value = false;
        emit("closed");
      } catch (e) {
        error.value =
          e.message || proxy.$t("reusable.aiMatterBuilderModal.saveError");
      } finally {
        saving.value = false;
      }
    }

    return {
      visible,
      loading,
      generating,
      saving,
      error,
      descriptors,
      busy,
      hasPendingItems,
      open,
      close,
      restoreDescriptor,
      regenerateDescriptor,
      save,
    };
  },
};
</script>

<style scoped src="./ai-builder/ai-builder.css"></style>
